/**
 * ConfigDashboardsModal
 *
 * "Configure Dashboards" modal — lists the project's dashboards with per-row
 * Reset / Archive actions and an Add Dashboard button at the bottom-right.
 *
 * Layout matches the XD: title + Search Dashboards in the header, table with
 * Dashboard Name / Details columns and a "View Archive" link at the right of
 * the header row, drag handles on the left of each row. Reordering via
 * @dnd-kit/sortable auto-saves on drop — there is no explicit Save button.
 */

import { useState, useMemo } from 'react'
import { GripVertical, X, Search } from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { dashboardApi } from '../../lib/dashboardApi'
import { projectsApi } from '../../lib/projectsApi'

// ─── Ghost row shown inside DragOverlay while dragging ────────────────────────
function GhostRow({ template }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white border border-[#358ECD]
                    rounded shadow-2xl ring-1 ring-[#358ECD]/20 opacity-95 text-sm">
      <GripVertical size={16} className="text-[#358ECD] flex-shrink-0" aria-hidden="true" />
      <span className="font-medium text-gray-800 flex-1 truncate">{template.name}</span>
      <span className="text-gray-400 text-xs truncate max-w-[280px]">
        {`Created from ${template.name} Template`}
      </span>
    </div>
  )
}

// ─── Sortable table row ───────────────────────────────────────────────────────
function SortableRow({ template, isDragActive, isBusy, onReset, onArchive }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: template.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0 : 1,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors select-none"
    >
      {/* Grip — only this cell starts a drag, so action links stay clickable */}
      <td
        className="px-3 py-3 w-8"
        style={{ cursor: isDragActive ? (isDragging ? 'grabbing' : 'grab') : 'grab' }}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} className="text-gray-300 mx-auto pointer-events-none" />
      </td>

      {/* Dashboard name */}
      <td className="px-3 py-3 text-sm text-gray-800">{template.name}</td>

      {/* Details */}
      <td className="px-3 py-3 text-sm text-gray-500">
        {`Created from ${template.name} Template`}
      </td>

      {/* Per-row actions */}
      <td className="px-3 py-3 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={() => onReset?.(template)}
          className="text-[#358ECD] hover:text-[#2c7ab2] text-sm transition-colors mr-6"
        >
          Reset to Default
        </button>
        <button
          type="button"
          disabled={isBusy}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onArchive?.(template) }}
          className="text-[#358ECD] hover:text-[#2c7ab2] text-sm transition-colors disabled:opacity-50"
        >
          {isBusy ? 'Archiving…' : 'Archive'}
        </button>
      </td>
    </tr>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export default function ConfigDashboardsModal({
  open,
  projectId,
  templates,
  onClose,
  onSaved,           // (reorderedTemplates) → update parent templates state
  onAddDashboard,
  onResetDashboard,  // (template) → reset that dashboard to defaults
  onArchiveChanged,  // () → optional notify parent after archive/restore so it can refetch templates
}) {
  const [items, setItems]       = useState(() => [...(templates || [])])
  const [activeId, setActiveId] = useState(null)
  const [search, setSearch]     = useState('')
  const [error, setError]       = useState(null)
  // 'active' = current dashboards table | 'archive' = archived dashboards list
  const [view, setView] = useState('active')
  const [archivedItems, setArchivedItems] = useState([])
  const [archiveLoading, setArchiveLoading] = useState(false)
  const [busyId, setBusyId] = useState(null)  // template id being archived/restored

  const activeTemplate = items.find((t) => t.id === activeId) || null
  const isSearching    = search.trim().length > 0

  const filteredItems = useMemo(() => {
    if (!isSearching) return items
    const q = search.toLowerCase()
    return items.filter((t) => t.name.toLowerCase().includes(q))
  }, [items, search, isSearching])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragStart(event) {
    setActiveId(event.active.id)
  }

  async function handleDragEnd(event) {
    const { active, over } = event
    setActiveId(null)
    if (!over || active.id === over.id) return

    const oldIdx = items.findIndex((r) => r.id === active.id)
    const newIdx = items.findIndex((r) => r.id === over.id)
    const reordered = arrayMove(items, oldIdx, newIdx)
    setItems(reordered)

    // Auto-save the new order (XD doesn't show an explicit Save button)
    try {
      await dashboardApi.saveNavOrder(projectId, reordered.map((t) => t.id))
      onSaved?.([...reordered])
      setError(null)
    } catch (err) {
      // Revert on failure
      setItems(items)
      setError(err.response?.data?.detail || 'Failed to save order. Please try again.')
    }
  }

  function handleDragCancel() {
    setActiveId(null)
  }

  async function loadArchived() {
    if (!projectId) return
    setArchiveLoading(true)
    setError(null)
    try {
      const res = await projectsApi.listArchivedDashboards(projectId)
      setArchivedItems(Array.isArray(res?.dashboards) ? res.dashboards : [])
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load archived dashboards.')
      setArchivedItems([])
    } finally {
      setArchiveLoading(false)
    }
  }

  async function handleArchive(template) {
    if (!projectId || !template?.id) return
    if (!window.confirm(`Archive "${template.name}"? You can restore it from View Archive.`)) return
    setBusyId(template.id)
    setError(null)
    try {
      await projectsApi.archiveProjectDashboard(projectId, template.id)
      // Remove from local list immediately so the UI feels responsive
      setItems(prev => prev.filter(t => t.id !== template.id))
      onArchiveChanged?.()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to archive dashboard.')
    } finally {
      setBusyId(null)
    }
  }

  async function handleRestore(template) {
    if (!projectId || !template?.id) return
    setBusyId(template.id)
    setError(null)
    try {
      await projectsApi.restoreProjectDashboard(projectId, template.id)
      setArchivedItems(prev => prev.filter(t => t.id !== template.id))
      // Add back to active list (parent should refetch via onArchiveChanged)
      setItems(prev => [...prev, template])
      onArchiveChanged?.()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to restore dashboard.')
    } finally {
      setBusyId(null)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh]
                   overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="config-dash-title"
      >
        {/* ── Header: title + search + close ── */}
        <div className="px-6 pt-5 pb-4 flex items-center gap-4 flex-shrink-0">
          <h2 id="config-dash-title" className="text-lg font-bold text-gray-800">
            Configure Dashboards
          </h2>
          <div className="flex-1" />
          <div className="relative w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Dashboards"
              className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded text-sm
                         focus:outline-none focus:ring-2 focus:ring-[#358ECD]"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 px-6 pb-4">
          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Column headers + View Archive / Back link */}
          <div className="flex items-center px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
            <div className="w-8" />
            <div className="flex-1 pl-0">Dashboard Name</div>
            <div className="flex-1">Details</div>
            <button
              type="button"
              onClick={async () => {
                if (view === 'active') {
                  setView('archive')
                  await loadArchived()
                } else {
                  setView('active')
                }
              }}
              className="text-[#358ECD] hover:text-[#2c7ab2] font-medium whitespace-nowrap"
            >
              {view === 'active' ? 'View Archive' : '← Back to Active'}
            </button>
          </div>

          {/* ── Archive view ── */}
          {view === 'archive' ? (
            archiveLoading ? (
              <p className="px-4 py-8 text-center text-gray-400 text-sm">Loading archived dashboards…</p>
            ) : archivedItems.length === 0 ? (
              <p className="px-4 py-8 text-center text-gray-400 text-sm">No archived dashboards.</p>
            ) : (
              <table className="w-full">
                <tbody>
                  {archivedItems.map((t) => (
                    <tr key={t.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-3 w-8" />
                      <td className="px-3 py-3 text-sm text-gray-800">{t.name}</td>
                      <td className="px-3 py-3 text-sm text-gray-500">
                        {t.description || `Created from ${t.name} Template`}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={busyId === t.id}
                          onClick={() => handleRestore(t)}
                          className="text-[#358ECD] hover:text-[#2c7ab2] text-sm transition-colors disabled:opacity-50"
                        >
                          {busyId === t.id ? 'Restoring…' : 'Restore'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : filteredItems.length === 0 ? (
            <p className="px-4 py-8 text-center text-gray-400 text-sm">
              {isSearching ? 'No dashboards match your search.' : 'No dashboards.'}
            </p>
          ) : isSearching ? (
            /* ── Static (non-draggable) filtered view ── */
            <table className="w-full">
              <tbody>
                {filteredItems.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="px-3 py-3 w-8">
                      <GripVertical size={14} className="text-gray-200 mx-auto" />
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-800">{t.name}</td>
                    <td className="px-3 py-3 text-sm text-gray-500">
                      {`Created from ${t.name} Template`}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onResetDashboard?.(t)}
                        className="text-[#358ECD] hover:text-[#2c7ab2] text-sm transition-colors mr-6"
                      >
                        Reset to Default
                      </button>
                      <button
                        type="button"
                        disabled={busyId === t.id}
                        onClick={() => handleArchive(t)}
                        className="text-[#358ECD] hover:text-[#2c7ab2] text-sm transition-colors disabled:opacity-50"
                      >
                        {busyId === t.id ? 'Archiving…' : 'Archive'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            /* ── Drag-and-drop table (auto-saves on drop) ── */
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={items.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <table className="w-full">
                  <tbody>
                    {items.map((template) => (
                      <SortableRow
                        key={template.id}
                        template={template}
                        isDragActive={activeId !== null}
                        isBusy={busyId === template.id}
                        onReset={onResetDashboard}
                        onArchive={handleArchive}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>

              <DragOverlay dropAnimation={null}>
                {activeTemplate ? <GhostRow template={activeTemplate} /> : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>

        {/* ── Footer: Add Dashboard (bottom-right) ── */}
        <div className="px-6 py-4 flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onAddDashboard}
            className="px-6 py-2 bg-[#358ECD] hover:bg-[#2c7ab2] text-white text-sm
                       font-semibold rounded transition-colors"
          >
            Add Dashboard
          </button>
        </div>
      </div>
    </div>
  )
}
